// #336, decision 5 (brief §15 rows 15-18): a client that does not know the
// program fields added by the v3 editor must not save over them, or it drops
// them in silence (#88). The API asks one question, answered here so it and
// the clients read one rule: for each new field this body's `schemaVersion`
// does not know, does it send a stored slot (or the program) back without it?
// Keep free of React and any UI import.

import type { SlotDraft } from "./programEdit.js";
import { amrapOf, isCellAbsent, kindAt } from "./programEditBuild.js";
import { hasPerRoundSetCounts } from "./programEditRoundSets.js";
import { cellRepsRange } from "./programRepsRange.js";
import { usesRoundTypes } from "./programRoundTypes.js";

/** What the v3 editor writes in `schemaVersion` on POST and PATCH. A body
 *  without it counts as 1. 2 shipped in 5.41.0 with S1 and Q2 only; 3 adds
 *  S2 and S3. 4 is the AMRAP stop rule (#355), which the Draft carries. 5 is
 *  the program's focus and origin (#356). 6 is the slot's model row (rowId,
 *  #356). 7 is the slot's side (#75). 8 is the pause inside the rep (HI-8). */
export const PROGRAM_SCHEMA_VERSION = 8;

/** The schemaVersion each new field came with. A body of that version or
 *  later knows the field, and may clear it on purpose; an older body that
 *  sends it back without it has dropped it on read. */
const FIELD_SINCE = { S1: 2, Q2: 2, S2: 3, S3: 3, AS: 4, FO: 5, RI: 6, SD: 7, PA: 8 } as const;

/** A body of this version does not know `field`: the content rule applies. */
const predates = (body: { schemaVersion?: number }, field: keyof typeof FIELD_SINCE): boolean =>
  (body.schemaVersion ?? 1) < FIELD_SINCE[field];

type SetLike = {
  intensity: { kind: string };
  repsMin?: number;
  repsMax?: number;
  amrap?: boolean;
  amrapStop?: unknown;
  pause?: unknown;
};
type SlotLike = { weeks: ReadonlyArray<{ sets: ReadonlyArray<SetLike> }>; rowId?: string; side?: string };
type ProgramLike<S> = {
  roundTypes?: ReadonlyArray<string>;
  focus?: string;
  origin?: unknown;
  schemaVersion?: number;
  sessions: ReadonlyArray<{ slots: ReadonlyArray<S> }>;
};

/** The rounds a slot runs in: an empty round does not count. */
const liveRounds = (slot: SlotLike) => slot.weeks.filter((w) => w.sets.length > 0);

/** What each slot-level field looks like on a stored or sent slot:
 *  - S1, its rounds do not all have the same number of sets;
 *  - Q2, mixed axes: the set at one position has another load axis in
 *    another round (kg in W1-W3, RIR in W4);
 *  - S3, a set has a rep range (either end);
 *  - AS, a set has an AMRAP stop rule;
 *  - RI, the slot has its model row;
 *  - SD, the slot has a side;
 *  - PA, a set has a pause inside the rep. */
const SLOT_FIELDS: Record<"S1" | "Q2" | "S3" | "AS" | "RI" | "SD" | "PA", (slot: SlotLike) => boolean> = {
  S1: (slot) => new Set(liveRounds(slot).map((w) => w.sets.length)).size > 1,
  Q2: (slot) => {
    const rounds = liveRounds(slot);
    const longest = Math.max(0, ...rounds.map((w) => w.sets.length));
    for (let i = 0; i < longest; i += 1) {
      const kinds = new Set(rounds.flatMap((w) => (w.sets[i] ? [w.sets[i]!.intensity.kind] : [])));
      if (kinds.size > 1) return true;
    }
    return false;
  },
  S3: (slot) =>
    slot.weeks.some((w) => w.sets.some((s) => s.repsMin !== undefined || s.repsMax !== undefined)),
  AS: (slot) => slot.weeks.some((w) => w.sets.some((s) => s.amrap === true && s.amrapStop !== undefined)),
  RI: (slot) => slot.rowId !== undefined,
  SD: (slot) => slot.side !== undefined,
  PA: (slot) => slot.weeks.some((w) => w.sets.some((s) => s.pause !== undefined)),
};
const SLOT_FIELD_NAMES = ["S1", "Q2", "S3", "AS", "RI", "SD", "PA"] as const;

/** One slot uses a field an old client loses on save (any of SLOT_FIELDS). */
export const slotUsesNewFields = (slot: SlotLike): boolean =>
  SLOT_FIELD_NAMES.some((f) => SLOT_FIELDS[f](slot));

/** The Draft twin of slotUsesNewFields, for the wide editor's lock (Q8):
 *  the same seven questions on the slot and its rows, so what it locks is what an old
 *  client would lose. Rounds the slot does not run in and sets a round does
 *  not have are not read. */
export const draftSlotUsesNewFields = (slot: SlotDraft): boolean => {
  if (hasPerRoundSetCounts(slot) || slot.rowId !== undefined) return true;
  if (slot.side !== undefined) return true;
  const live = (w: number) => !slot.inactiveWeeks[w];
  return slot.setRows.some((row) => {
    const weeks = row.cells.flatMap((_, w) => (live(w) && !isCellAbsent(row, w) ? [w] : []));
    if (weeks.some((w) => cellRepsRange(row, row.cells[w]!) !== null)) return true;
    if (weeks.some((w) => amrapOf(row.cells[w]!) && row.cells[w]!.amrapStop !== undefined)) return true;
    if (weeks.some((w) => row.cells[w]!.pausePhase !== undefined)) return true;
    return new Set(weeks.map((w) => kindAt(row, w))).size > 1;
  });
};

/** `body` drops a field it does not know (by its schemaVersion), field by
 *  field: a slot of `stored` has it, and the slot with the same id in `body`
 *  does not. A field another slot still has does not cover the loss. A slot
 *  left out of the body is a removal, not a drop; a body slot without an id
 *  is a new one. S2 is the program's: stored round types, and a body with
 *  none. FO too: a stored focus or origin, and a body without it. */
export const programDropsNewFields = (
  stored: ProgramLike<SlotLike & { id: string }>,
  body: ProgramLike<SlotLike & { id?: string }>,
): boolean => {
  if (predates(body, "S2") && usesRoundTypes(stored) && !usesRoundTypes(body)) return true;
  if (predates(body, "FO") && ((stored.focus && !body.focus) || (stored.origin && !body.origin))) return true;
  const unknown = SLOT_FIELD_NAMES.filter((f) => predates(body, f)).map((f) => SLOT_FIELDS[f]);
  if (unknown.length === 0) return false;
  const sent = new Map(
    body.sessions.flatMap((s) => s.slots.flatMap((sl) => (sl.id ? [[sl.id, sl] as const] : []))),
  );
  return stored.sessions.some((s) =>
    s.slots.some((sl) => {
      const now = sent.get(sl.id);
      return now !== undefined && unknown.some((uses) => uses(sl) && !uses(now));
    }),
  );
};

/** The body comes from an editor that knows every new field. */
export const isCurrentSchemaBody = (body: { schemaVersion?: number }): boolean =>
  (body.schemaVersion ?? 1) >= PROGRAM_SCHEMA_VERSION;

type PlanSetLike = {
  repsMin?: number;
  repsMax?: number;
  work?: unknown;
  restSec?: number;
  amrapStop?: unknown;
  pause?: unknown;
};
type PlanLike = { entries: ReadonlyArray<{ exerciseId: string; sets: ReadonlyArray<PlanSetLike> }> };
const PLAN_SET_FIELDS = ["repsMin", "repsMax", "work", "restSec", "amrapStop", "pause"] as const;

/** #530: the plan twin of programDropsNewFields. A plan PATCH is a replace, so a
 *  client that rebuilds the plan without the per-set fields it did not read
 *  (rep range, work, rest, AMRAP stop, pause) loses them in silence. The body drops
 *  one when the entry at the same position (same exercise) has the set at the
 *  same index without a field the stored set has. The comparison is positional
 *  because a Plan's entries and sets carry no id, unlike a program's slots. A body of the current
 *  schemaVersion knows them all and may remove them on purpose. */
export const planDropsNewFields = (
  stored: PlanLike,
  body: PlanLike & { schemaVersion?: number },
): boolean => {
  if (isCurrentSchemaBody(body)) return false;
  return stored.entries.some((entry, i) => {
    const now = body.entries[i];
    if (!now || now.exerciseId !== entry.exerciseId) return false;
    return entry.sets.some((set, j) => {
      const sent = now.sets[j];
      return sent !== undefined && PLAN_SET_FIELDS.some((f) => set[f] !== undefined && sent[f] === undefined);
    });
  });
};
